import { IconChevronLeft } from '@tabler/icons-react'
import { Container } from '@mantine/core'
import { Anchor, Badge, Divider, Skeleton, Stack, Text, Title } from 'components'
import { useArcTranslation } from 'hooks'
import { Link, useParams } from 'react-router-dom'
import { PortableTextContent } from './components/PortableTextContent'
import { BACK_ICON_SIZE, BADGE_ICON_SIZE } from './constants'
import { getHubStyle } from './hubStyles'
import { useResourceArticle } from './hooks/useResourceArticle'

// Fixed violet and no underline, mirroring the prototype's back link.
const BackLink = ({ label }: { label: string }) => (
  <Anchor
    component={Link}
    to='/app/resource-center'
    size='sm'
    mb='lg'
    sx={theme => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      color: theme.colors.violet[6],
      '&:hover': { textDecoration: 'none' },
    })}
  >
    <IconChevronLeft size={BACK_ICON_SIZE} />
    {label}
  </Anchor>
)

// Article detail view: back link, hub badge, title, lead paragraph and the
// Portable Text body.
export const ResourceArticleContent = () => {
  const { articleSlug } = useParams<{ articleSlug: string }>()
  const { t } = useArcTranslation()
  const { article, isLoading, isError } = useResourceArticle(articleSlug)

  if (isLoading) {
    return (
      <Container size='xl' pt='md' pb='xl'>
        <Stack spacing='md'>
          <Skeleton height={20} width={120} />
          <Skeleton height={40} width='70%' />
          <Skeleton height={16} />
          <Skeleton height={200} radius='md' />
        </Stack>
      </Container>
    )
  }

  if (isError || !article) {
    return (
      <Container size='xl' pt='md' pb='xl'>
        <BackLink label={t('resource_center.back')} />
        <Text color='dimmed'>{t(isError ? 'resource_center.load_error' : 'resource_center.not_found')}</Text>
      </Container>
    )
  }

  const hubStyle = article.hub ? getHubStyle(article.hub.slug, article.hub.color, article.hub.icon) : null
  const HubIcon = hubStyle?.icon

  return (
    <Container size='xl' pt='md' pb='xl'>
      <BackLink label={t('resource_center.back')} />

      <Stack spacing='xs' mb='lg'>
        {article.hub && hubStyle && HubIcon ? (
          <Anchor component={Link} to='/app/resource-center' sx={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
            <Badge
              variant='light'
              color={hubStyle.color}
              size='sm'
              leftSection={<HubIcon size={BADGE_ICON_SIZE} />}
              sx={{ cursor: 'pointer' }}
            >
              {article.hub.title}
            </Badge>
          </Anchor>
        ) : null}
        <Title order={1}>{article.title}</Title>
        {article.excerpt ? (
          <Text color='dimmed' size='lg'>
            {article.excerpt}
          </Text>
        ) : null}
      </Stack>

      <Divider mb='xl' />

      {article.content && article.content.length > 0 ? (
        <PortableTextContent value={article.content} />
      ) : (
        <Text color='dimmed'>{t('resource_center.article_empty')}</Text>
      )}
    </Container>
  )
}
